import React, { useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { getLineHeight, wrapTextChildNodesWithSpan } from '../utils/dom';
var JsEllipsis = /*#__PURE__*/forwardRef(function (props, ref) {
  var prefixCls = props.prefixCls,
      text = props.text,
      ellipsis = props.ellipsis,
      dangerouslyUseInnerHTML = props.dangerouslyUseInnerHTML,
      _props$maxLine = props.maxLine,
      maxLine = _props$maxLine === void 0 ? 1 : _props$maxLine,
      maxHeight = props.maxHeight,
      ellipsisNode = props.ellipsisNode,
      collapseNode = props.collapseNode,
      _props$endExcludes = props.endExcludes,
      endExcludes = _props$endExcludes === void 0 ? [] : _props$endExcludes,
      reflowOnResize = props.reflowOnResize,
      onReflow = props.onReflow,
      onEllipsisNodeClick = props.onEllipsisNodeClick,
      onCollapseNodeClick = props.onCollapseNodeClick;
  var truncating = useRef(false);
  var domRef = useRef(null);
  var textRef = useRef(null);
  var ellipsisRef = useRef(null);
  var lineHeightRef = useRef(0);
  var setCurLineHeight = useCallback(function () {
    if (domRef.current) {
      lineHeightRef.current = getLineHeight(domRef.current);
    }
  }, []);
  useEffect(function () {
    setCurLineHeight();
  }, []);

  function handleOnReflow(isEllipsis, result) {
    if (onReflow && typeof onReflow === 'function') {
      onReflow(isEllipsis, result);
    }
  }

  function truncateText(container, textContainer, max) {
    var content = textContainer.textContent || '';
    var currentText = '';
    var l = 0;
    var r = content.length - 1; // Binary truncate text until get the max limit fragment of text.

    while (l < r) {
      var m = Math.floor((l + r) / 2);

      if (l === m) {
        break;
      }

      var temp = content.slice(l, m);
      textContainer.innerText = currentText + temp;

      var _container$getBoundin = container.getBoundingClientRect(),
          height = _container$getBoundin.height;

      if (height > max) {
        r = m;
      } else {
        currentText += temp;
        l = m;
      }
    } // Remove the exclude char at the end of the content.


    while (endExcludes.includes(currentText[currentText.length - 1])) {
      currentText = currentText.slice(0, -1);
    } // Callback after reflow.


    handleOnReflow(true, currentText);
    textContainer.innerText = currentText;
  }

  function truncateHTML(container, textContainer, max) {
    // only enter this function when container overflow.
    var children = textContainer.childNodes;

    if (children.length === 1) {
      var node = children[0];

      if (node.nodeType === Node.TEXT_NODE) {
        truncateText(container, textContainer, max);
      } else {
        var html = node.innerHTML; // clear content to determine whether the empty node can be placed.

        node.innerHTML = '';

        var _container$getBoundin2 = container.getBoundingClientRect(),
            height = _container$getBoundin2.height;

        if (height > max) {
          // return after remove the node, if overflow with empty node.
          textContainer.removeChild(node);
          handleOnReflow(true, textContainer.innerHTML);
          return;
        }

        node.innerHTML = html; // recursive truncate node

        truncateHTML(container, node, max);
      }
    } else {
      var nodes = [].slice.call(children);
      textContainer.innerHTML = '';
      var i = 0; // find the critical node

      while (i < nodes.length) {
        textContainer.appendChild(nodes[i]);

        var _container$getBoundin3 = container.getBoundingClientRect(),
            _height = _container$getBoundin3.height;

        if (_height > max) {
          break;
        }

        i++;
      }

      if (textContainer.childNodes[i]) {
        // truncate the critical node
        truncateHTML(container, textContainer.childNodes[i], max);
      }
    }
  } // reflow when the main props change.


  var reflow = useCallback(function () {
    if (!domRef.current || !textRef.current || !ellipsisRef.current || truncating.current) {
      return;
    }

    ellipsisRef.current.style.display = 'none';

    if (dangerouslyUseInnerHTML) {
      textRef.current.innerHTML = text;
    } else {
      textRef.current.innerText = text;
    }

    if (!ellipsis) {
      return;
    }

    var _domRef$current$getBo = domRef.current.getBoundingClientRect(),
        height = _domRef$current$getBo.height;

    var max = isNaN(Number(maxHeight)) ? lineHeightRef.current * maxLine : Number(maxHeight);

    if (height <= max) {
      handleOnReflow(false, text);
      return;
    }

    truncating.current = true;
    ellipsisRef.current.style.display = 'inline';

    if (dangerouslyUseInnerHTML) {
      // wrap the text children node with span element.
      wrapTextChildNodesWithSpan(textRef.current, prefixCls + "-js-content-text");
      truncateHTML(domRef.current, textRef.current, max);
    } else {
      truncateText(domRef.current, textRef.current, max);
    }

    truncating.current = false;
  }, [text, ellipsis, dangerouslyUseInnerHTML, maxLine, maxHeight, ellipsisNode, endExcludes]);
  useEffect(function () {
    reflow();
  }, [reflow]);
  var observerRef = useRef(null); // Observe resize event of container if reflowOnResize is true.

  useEffect(function () {
    if (domRef.current && reflowOnResize) {
      if (!observerRef.current && ellipsis) {
        observerRef.current = new ResizeObserver(reflow);
        observerRef.current.observe(domRef.current);
      }
    }

    return function () {
      // Remove observer when component unmounted.
      if (observerRef.current && domRef.current) {
        observerRef.current.unobserve(domRef.current);
        observerRef.current = null;
      }
    };
  }, [ellipsis]);
  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current,
      reflow: reflow
    };
  }, [reflow]);
  return /*#__PURE__*/React.createElement("div", {
    ref: domRef,
    className: prefixCls + "-js"
  }, /*#__PURE__*/React.createElement("span", {
    ref: textRef,
    className: prefixCls + "-js-content"
  }), /*#__PURE__*/React.createElement("span", {
    ref: ellipsisRef,
    className: prefixCls + "-js-content-ellipsis",
    onClick: onEllipsisNodeClick
  }, ellipsisNode), !ellipsis && collapseNode && /*#__PURE__*/React.createElement("span", {
    className: prefixCls + "-js-content-collapse",
    onClick: onCollapseNodeClick
  }, collapseNode));
});
export default /*#__PURE__*/React.memo(JsEllipsis);